import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { KeyTakeawayCardStack } from 'apps/sigma2-ui/src/pages/FocusAreaPage/KeyTakeawayCardStack';
import { TreatmentSelectionModal } from 'apps/sigma2-ui/src/components/TreatmentSelectionModal';
import { useToggle } from 'apps/sigma2-ui/src/lib/hooks/useToggle';
import { FocusAreaSummaryPolarChart } from 'apps/sigma2-ui/src/focus-area/FocusAreaSummaryPolarChart';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { BlueTopFilterAndMetricBar } from 'apps/sigma2-ui/src/components/focus-area/BlueTopFilterAndMetricBar';
import { useDynamicFilters } from 'apps/sigma2-ui/src/lib/hooks/useDynamicFilters';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { FocusAreaSummaryBarChart } from 'apps/sigma2-ui/src/focus-area/FocusAreaSummaryBarChart';
import { IconRadarChartSvg } from 'apps/sigma2-ui/src/components/svg/IconRadarChart';
import { IconBarChartSvg } from 'apps/sigma2-ui/src/components/svg/IconBarChart';
import cn from 'classnames';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { SolutionCardStack } from 'apps/sigma2-ui/src/components/overview/SolutionsCardStack';
import { useDynamicMetrics } from 'apps/sigma2-ui/src/lib/hooks/useDynamicMetrics';
import { focusAreaPersistedFilterCacheKey } from 'apps/sigma2-ui/src/constants/usable-cache-keys';
import { focusAreaPersistedMetricCacheKey } from 'apps/sigma2-ui/src/constants/usable-cache-keys';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { useShouldShowRadarChart } from 'apps/sigma2-ui/src/lib/hooks/useShouldShowRadarChart';
import { RoutableLink } from 'apps/sigma2-ui/src/components/ui/RoutableLink';
import { FocusAreaSummaryDescription } from 'apps/sigma2-ui/src/focus-area/FocusAreaSummaryDescription';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { FILTER_SET_DEFAULT_NAME } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { InsightWithFocusArea } from 'libs/end-to-end/src/insights';
import { Treatment } from 'libs/end-to-end/src/treatment';
import { EntitledDetailsForFocusAreaPage } from 'libs/end-to-end/src/focus-area-utils';
import { FilterInformation } from 'apps/sigma2-ui/src/lib/hooks/filter-state';

interface FocusAreaSummarySectionProps {
  focusAreaDefinition: MyFocusAreaDefinition;
  observation: MyObservation;
  entitledDetailsForFocusAreaPage: EntitledDetailsForFocusAreaPage;
  topTreatment: Treatment | null;
  keyTakeawayAchievements: InsightWithFocusArea[];
  keyTakeawayRisk: InsightWithFocusArea[];
  currentDisplayedMetric: string | null;
  setCurrentDisplayedMetric: (metric: string | null) => void;
}

export const FocusAreaSummarySection = ({
  focusAreaDefinition,
  observation,
  currentDisplayedMetric,
  setCurrentDisplayedMetric,
  entitledDetailsForFocusAreaPage,
  topTreatment,
  keyTakeawayAchievements,
  keyTakeawayRisk,
}: FocusAreaSummarySectionProps) => {
  const language = useLanguage();
  const navigate = useNavigateSafe();

  const { updateTreatments } = useObservation();

  const allFocusAreaDefinitions = useMemo(() => {
    return observation?.focusAreas || [];
  }, [observation]);

  const [
    keyTakeawaySelectionModalVisible,
    toggleKeyTakeawaySelectionModalVisible,
  ] = useToggle();

  const [initialPrimaryFilterValues, setInitialPrimaryFilterValues] = useState<
    string[] | null
  >(null);
  const [initialSecondaryFilterValues, setInitialSecondaryFilterValues] =
    useState<string[] | null>(null);
  const [initialSelectedMetric, setInitialSelectedMetric] = useState<
    string | null
  >(null);

  const [graphType, setGraphType] = useState<'radar' | 'bar'>('radar');

  const { primaryFilter, secondaryFilter, onSelect } = useDynamicFilters(
    useMemo(() => {
      return {
        automaticallySelectAll: true,
        persistKey: focusAreaPersistedFilterCacheKey({
          focusArea: focusAreaDefinition.focusArea,
          page: 'overview',
          webCacheKeyPrefix: observation.webCacheKeyPrefix,
        }),
        // TODO: Change this feature to support custom filter sets.
        filterSetName: FILTER_SET_DEFAULT_NAME,
      };
    }, [focusAreaDefinition, observation]),
  );
  const dynamicMetricsPayload = useDynamicMetrics(
    useMemo(() => {
      return {
        type: 'single',
        automaticallySelectFirstPreferred: true,
        focusArea: focusAreaDefinition.focusArea,
        persistKey: focusAreaPersistedMetricCacheKey({
          focusArea: focusAreaDefinition.focusArea,
          page: 'overview',
          webCacheKeyPrefix: observation.webCacheKeyPrefix,
        }),
      };
    }, [focusAreaDefinition, observation]),
  );

  const analytics = useAnalytics();

  useEffect(() => {
    analytics.trackEvent({
      eventName: 'Focus Area Page Viewed',
      eventProperties: {
        selectedFocusArea: focusAreaDefinition.focusArea,
        selectedTab: 'Summary',
      },
    });
  }, [analytics, focusAreaDefinition]);

  useEffect(() => {
    if (
      primaryFilter &&
      secondaryFilter &&
      initialPrimaryFilterValues === null &&
      initialSecondaryFilterValues === null
    ) {
      setInitialPrimaryFilterValues(primaryFilter.selectedValues);
      setInitialSecondaryFilterValues(secondaryFilter.selectedValues);
    }
    if (
      dynamicMetricsPayload.selectedMetric &&
      initialSelectedMetric === null
    ) {
      setInitialSelectedMetric(dynamicMetricsPayload.selectedMetric);
    }
  }, [
    dynamicMetricsPayload,
    initialPrimaryFilterValues,
    initialSecondaryFilterValues,
    initialSelectedMetric,
    primaryFilter,
    secondaryFilter,
  ]);

  useEffect(() => {
    if (
      currentDisplayedMetric === null &&
      dynamicMetricsPayload.selectedMetric !== null
    ) {
      setCurrentDisplayedMetric(dynamicMetricsPayload.selectedMetric);
    }
  }, [
    currentDisplayedMetric,
    dynamicMetricsPayload,
    setCurrentDisplayedMetric,
  ]);

  const onAnalyze = useCallback(() => {
    setInitialPrimaryFilterValues(primaryFilter?.selectedValues ?? null);
    setInitialSecondaryFilterValues(secondaryFilter?.selectedValues ?? null);
    setInitialSelectedMetric(dynamicMetricsPayload.selectedMetric ?? null);
    setCurrentDisplayedMetric(dynamicMetricsPayload.selectedMetric ?? null);
  }, [
    dynamicMetricsPayload.selectedMetric,
    primaryFilter?.selectedValues,
    secondaryFilter?.selectedValues,
    setCurrentDisplayedMetric,
  ]);

  // Memoized to fix the issue of the polar chart gradient not rendering properly
  const FocusAreaSummaryPolarChartComponent = useMemo(() => {
    if (!observation) {
      return <></>;
    }

    return (
      <FocusAreaSummaryPolarChart
        dimensions={observation?.dimensions || []}
        testSummary={observation?.testSummary || null}
        focusArea={focusAreaDefinition.focusArea}
        onDotClick={() =>
          navigate(`/${focusAreaDefinition.focusArea}/summary-v2`)
        }
        calibrationFraming={observation.calibrationFraming}
        metric={currentDisplayedMetric ?? null}
      />
    );
  }, [observation, focusAreaDefinition, navigate, currentDisplayedMetric]);

  const FocusAreaSummaryBarChartComponent = useMemo(() => {
    if (!observation) {
      return <></>;
    }

    return (
      <FocusAreaSummaryBarChart
        dimensions={observation.dimensions}
        testSummary={observation.testSummary}
        focusArea={focusAreaDefinition.focusArea}
        calibrationFraming={observation.calibrationFraming}
        metric={currentDisplayedMetric ?? null}
      />
    );
  }, [observation, focusAreaDefinition, currentDisplayedMetric]);

  const shouldShowRadarChart = useShouldShowRadarChart(
    observation?.dimensions.length,
  );

  const keyTakeawayAchievementsEnabled = useMemo(
    () =>
      keyTakeawayAchievements.length > 0 &&
      entitledDetailsForFocusAreaPage.entitledToBiasAnalysis,
    [
      entitledDetailsForFocusAreaPage.entitledToBiasAnalysis,
      keyTakeawayAchievements.length,
    ],
  );

  const keyTakeawayRiskEnabled = useMemo(
    () =>
      keyTakeawayRisk.length > 0 &&
      entitledDetailsForFocusAreaPage.entitledToBiasAnalysis,
    [
      entitledDetailsForFocusAreaPage.entitledToBiasAnalysis,
      keyTakeawayRisk.length,
    ],
  );
  const topTreatmentEnabled = useMemo(() => {
    return (
      topTreatment !== null &&
      entitledDetailsForFocusAreaPage.areSolutionsEnabled
    );
  }, [entitledDetailsForFocusAreaPage.areSolutionsEnabled, topTreatment]);

  if (!observation) {
    return <></>;
  }

  const hasFilterChanged = ({
    filter,
    initialFilterValues,
  }: {
    filter: FilterInformation | null;
    initialFilterValues: string[] | null;
  }) => {
    if (!filter) {
      return false;
    }
    return (
      filter &&
      initialFilterValues &&
      filter.selectedValues.sort().join() !== initialFilterValues.sort().join()
    );
  };

  return (
    <Spacing direction="vertical" size="3xl">
      <UserAnalysesOverlay onUserAnalysisCancel={() => {}} />

      <Spacing direction="vertical" size="lg">
        {entitledDetailsForFocusAreaPage.atLeastOneSectionShowing && (
          <BlueTopFilterAndMetricBar
            primaryFilter={primaryFilter}
            secondaryFilter={secondaryFilter}
            dynamicMetrics={dynamicMetricsPayload.metrics}
            selectedMetric={dynamicMetricsPayload.selectedMetric ?? null}
            onSelectFilter={onSelect}
            onSelectMetric={dynamicMetricsPayload.onSelectSingle}
            onAnalyze={onAnalyze}
            isProcessing={false}
            disableAnalyze={() => {
              const primaryHasChanged = hasFilterChanged({
                filter: primaryFilter,
                initialFilterValues: initialPrimaryFilterValues,
              });
              const secondaryHasChanged = hasFilterChanged({
                filter: secondaryFilter,
                initialFilterValues: initialSecondaryFilterValues,
              });
              return (
                !primaryHasChanged &&
                !secondaryHasChanged &&
                dynamicMetricsPayload.selectedMetric === initialSelectedMetric
              );
            }}
          />
        )}

        {(keyTakeawayAchievementsEnabled ||
          keyTakeawayRiskEnabled ||
          topTreatmentEnabled ||
          entitledDetailsForFocusAreaPage.entitledToBiasAnalysis) && (
          <>
            <S2Typography
              variant="headline-5"
              translatedContent={language.keyInsights()}
            />

            <Spacing direction="vertical" size="none">
              <Spacing direction="horizontal" size="md">
                {keyTakeawayAchievementsEnabled && (
                  <div className="flex-0 w-1/3">
                    <KeyTakeawayCardStack
                      variant="achievements"
                      items={keyTakeawayAchievements}
                      calibrationFraming={observation.calibrationFraming}
                    />
                  </div>
                )}

                {keyTakeawayRiskEnabled && (
                  <div className="flex-0 w-1/3">
                    <KeyTakeawayCardStack
                      variant="risks"
                      items={keyTakeawayRisk}
                      calibrationFraming={observation.calibrationFraming}
                    />
                  </div>
                )}

                {topTreatment && topTreatmentEnabled && (
                  <div className="flex-0 w-1/3">
                    <SolutionCardStack
                      treatments={[topTreatment]}
                      onTreatmentTitleClick={
                        toggleKeyTakeawaySelectionModalVisible
                      }
                      currentTreatmentIndex={0}
                      pagination={null}
                      allFocusAreaDefinitions={allFocusAreaDefinitions}
                    />
                  </div>
                )}

                <TreatmentSelectionModal
                  maxWidthOverride="max-w-screen-md"
                  show={keyTakeawaySelectionModalVisible}
                  title={language.focusAreaKeyTakeawayCardSolutionTitle()}
                  topTreatments={{
                    treatments: topTreatment ? [topTreatment] : [],
                  }}
                  updateTreatments={updateTreatments}
                  onClose={toggleKeyTakeawaySelectionModalVisible}
                />
              </Spacing>

              {entitledDetailsForFocusAreaPage.entitledToBiasAnalysis && (
                <div className="flex justify-center pt-6">
                  <RoutableLink
                    to={`/${focusAreaDefinition.focusArea}/insights`}
                    className="font-sans text-tertiary font-medium text-sm xl:text-base flex flex-row items-center gap-[5px]"
                  >
                    <S2Typography
                      variant="link"
                      translatedContent={language.focusAreaViewAllInsights()}
                    />
                  </RoutableLink>
                </div>
              )}
            </Spacing>
          </>
        )}
      </Spacing>

      {entitledDetailsForFocusAreaPage.entitledToBiasAnalysis && (
        <Spacing
          direction="horizontal"
          size="gap-2"
          className="rounded-lg bg-[#FCFCFC] justify-between"
        >
          <div className="min-w-[28rem]">
            {currentDisplayedMetric && observation && (
              <FocusAreaSummaryDescription
                focusAreaDefinition={focusAreaDefinition}
                metric={currentDisplayedMetric}
                myObservation={observation}
                calibrationFraming={observation.calibrationFraming}
              />
            )}
          </div>

          <Spacing
            size="sm"
            direction="vertical"
            className="justify-center items-center w-full"
          >
            <Spacing
              direction="horizontal"
              size="xs"
              className="w-full flex-row-reverse p-4"
            >
              {shouldShowRadarChart && (
                <>
                  <div
                    className={cn('p-2 rounded', {
                      'bg-royal-200 text-royal-700': graphType === 'bar',
                    })}
                    onClick={() => {
                      setGraphType('bar');
                    }}
                  >
                    <IconBarChartSvg svg={{ className: 'w-6 h-6' }} />
                  </div>
                  <div
                    className={cn('p-2 rounded', {
                      'bg-royal-200 text-royal-700': graphType === 'radar',
                    })}
                    onClick={() => {
                      setGraphType('radar');
                    }}
                  >
                    <IconRadarChartSvg svg={{ className: 'w-6 h-6' }} />
                  </div>
                </>
              )}
            </Spacing>
            <div className="min-w-[40rem] h-full">
              {graphType === 'radar' && shouldShowRadarChart
                ? FocusAreaSummaryPolarChartComponent
                : FocusAreaSummaryBarChartComponent}
            </div>
          </Spacing>
        </Spacing>
      )}
    </Spacing>
  );
};
